import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import styled from 'styled-components';
import { Scrim, Modal, Btn, Input, Muted } from '../ui/primitives';
import { fetchCampaignOrgs, assignCampaignOrgs } from '../services/campaignAdminApi';

/**
 * Org picker for a campaign. Loads every org (pre-checking those already attached),
 * lets the super admin select a set, and PUTs the exact list back — attaching new
 * orgs and detaching de-selected ones. Calls `onSaved()` after a successful save.
 */
export default function CampaignOrgAssignDialog({ open, campaign, onClose, onSaved }) {
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !campaign?.campaignId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setQuery('');
    fetchCampaignOrgs(campaign.campaignId)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.orgs) ? data.orgs : [];
        setOrgs(list);
        setSelected(new Set(list.filter((o) => o.attached).map((o) => o.orgId)));
      })
      .catch((e) => !cancelled && setError(e.message || 'Failed to load organizations.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, campaign]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter(
      (o) =>
        (o.orgName || '').toLowerCase().includes(q) ||
        (o.orgId || '').toLowerCase().includes(q),
    );
  }, [orgs, query]);

  const toggle = (orgId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await assignCampaignOrgs(campaign.campaignId, Array.from(selected));
      onSaved?.();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Scrim onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <Modal style={{ maxWidth: 560 }}>
        <div className="modal__head">
          <div>
            <h3>Assign organizations</h3>
            <p>{campaign?.name} — pick the orgs this campaign applies to.</p>
          </div>
          <button className="x-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal__body">
          {error && (
            <div style={{ color: 'crimson', fontSize: 13, marginBottom: 12 }}>{error}</div>
          )}

          <Input
            placeholder="Search organizations…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ marginBottom: 12 }}
          />

          {loading ? (
            <LoadingWrap><CircularProgress size={26} /></LoadingWrap>
          ) : (
            <>
              <SelCount>
                <b>{selected.size}</b> selected
                <Muted style={{ marginLeft: 6 }}>of {orgs.length} orgs</Muted>
              </SelCount>
              <OrgList>
                {filtered.length === 0 && (
                  <Muted style={{ padding: '12px 4px' }}>No organizations match.</Muted>
                )}
                {filtered.map((o) => (
                  <OrgRow key={o.orgId} onClick={() => toggle(o.orgId)}>
                    <input
                      type="checkbox"
                      checked={selected.has(o.orgId)}
                      onChange={() => toggle(o.orgId)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div>
                      <div className="org-name">{o.orgName || '(unnamed org)'}</div>
                      <div className="org-id">{o.orgId}</div>
                    </div>
                  </OrgRow>
                ))}
              </OrgList>
            </>
          )}
        </div>

        <div className="modal__foot">
          <Btn $variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={handleSave} disabled={saving || loading}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save assignment'}
          </Btn>
        </div>
      </Modal>
    </Scrim>
  );
}

const LoadingWrap = styled.div`
  display: grid;
  place-items: center;
  padding: 32px 0;
`;

const SelCount = styled.div`
  font-size: 12.5px;
  margin-bottom: 8px;
`;

const OrgList = styled.div`
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
`;

const OrgRow = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  &:last-child { border-bottom: none; }
  &:hover { background: ${({ theme }) => theme.surface2}; }
  .org-name { font-size: 13.5px; font-weight: 600; color: ${({ theme }) => theme.text}; }
  .org-id { font-size: 11px; color: ${({ theme }) => theme.textMuted}; }
`;
